import { Equal, SlidersHorizontal } from "lucide-react-native";
import { FC, useCallback, useMemo, useState } from "react";
import { ScrollView } from "react-native";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Box,
  Button,
  ButtonText,
  Heading,
  HStack,
  Pressable,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { DemographicDistribution } from "@/types/resources";

/* ── Types ────────────────────────────────────────── */

interface DemographicDistributionEditorProps {
  disabled?: boolean;
  distribution: DemographicDistribution[];
  onChange: (distribution: DemographicDistribution[]) => void;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  title: string;
  totalOptions?: number;
}

/* ── Helpers ──────────────────────────────────────── */

const setWeight = (
  items: DemographicDistribution[],
  changedIndex: number,
  pct: number
): DemographicDistribution[] =>
  items.map((item, i) =>
    i === changedIndex ? { ...item, weight: pct / 100 } : item
  );

const distributeEqually = (
  items: DemographicDistribution[]
): DemographicDistribution[] => {
  if (items.length === 0) return items;
  const base = Math.floor(100 / items.length);
  const remainder = 100 - base * items.length;
  return items.map((item, i) => ({
    ...item,
    weight: (base + (i < remainder ? 1 : 0)) / 100,
  }));
};

/* ── Component ────────────────────────────────────── */

const DemographicDistributionEditor: FC<DemographicDistributionEditorProps> = ({
  disabled = false,
  distribution,
  onChange,
  onOpenChange,
  open,
  title,
  totalOptions,
}) => {
  const { i18n } = useLocalization();
  const [internalOpen, setInternalOpen] = useState(false);
  const [draft, setDraft] = useState<DemographicDistribution[]>([]);

  const isControlled = open !== undefined;
  const dialogOpen = isControlled ? open! : internalOpen;

  const initDraft = useCallback(
    () =>
      distribution.map((d) => ({
        ...d,
        weight: Math.round(d.weight * 100) / 100,
      })),
    [distribution]
  );

  // Initialize draft during render (not in an effect) when the controlled
  // dialog opens. Keyed only on open/controlled state to match the original
  // effect's dependencies.
  const draftSyncKey = `${isControlled}|${dialogOpen}`;
  const [prevDraftSyncKey, setPrevDraftSyncKey] = useState(draftSyncKey);
  if (prevDraftSyncKey !== draftSyncKey) {
    setPrevDraftSyncKey(draftSyncKey);
    if (isControlled && dialogOpen) setDraft(initDraft());
  }

  const handleOpen = useCallback(() => {
    setDraft(initDraft());
    if (isControlled) onOpenChange?.(true);
    else setInternalOpen(true);
  }, [initDraft, isControlled, onOpenChange]);

  const handleClose = useCallback(() => {
    if (isControlled) onOpenChange?.(false);
    else setInternalOpen(false);
  }, [isControlled, onOpenChange]);

  const handleApply = useCallback(() => {
    onChange(draft);
    if (isControlled) onOpenChange?.(false);
    else setInternalOpen(false);
  }, [draft, isControlled, onChange, onOpenChange]);

  const getMaxPct = useCallback(
    (index: number) => {
      const othersPct = draft.reduce(
        (s, d, i) => (i === index ? s : s + Math.round(d.weight * 100)),
        0
      );
      return Math.max(1, Math.min(99, 100 - othersPct));
    },
    [draft]
  );

  const handleSliderChange = useCallback(
    (index: number, rawPct: number) => {
      const pct = Math.max(1, Math.min(rawPct, getMaxPct(index)));
      setDraft((prev) => setWeight(prev, index, pct));
    },
    [getMaxPct]
  );

  const handleEqualize = useCallback(() => {
    setDraft((prev) => distributeEqually(prev));
  }, []);

  const totalWeight = useMemo(
    () => draft.reduce((s, d) => s + d.weight, 0),
    [draft]
  );
  const totalPct = Math.round(totalWeight * 100);
  const otherPct = Math.max(0, 100 - totalPct);

  if (distribution.length < 1) return null;

  return (
    <>
      {!isControlled && (
        <Pressable disabled={disabled} onPress={handleOpen}>
          <Box
            className={`w-7 h-7 rounded-md items-center justify-center bg-white border border-outline-200 ${disabled ? "opacity-40" : ""}`}
          >
            <SlidersHorizontal
              color="rgb(var(--color-typography-600))"
              size={13}
            />
          </Box>
        </Pressable>
      )}

      <AlertDialog isOpen={dialogOpen} onClose={handleClose}>
        <AlertDialogBackdrop />
        <AlertDialogContent className="gap-10 max-w-[420px]">
          <AlertDialogHeader>
            <HStack className="items-center justify-between w-full">
              <Heading size="lg">{title}</Heading>
              <Pressable onPress={handleEqualize}>
                <HStack className="items-center gap-1 px-2 py-1 rounded-full bg-background-100">
                  <Equal color="rgb(var(--color-typography-400))" size={10} />
                  <Text className="text-[11px] text-typography-400">
                    {i18n.t("audienceBuilder.equalizeWeights")}
                  </Text>
                </HStack>
              </Pressable>
            </HStack>
          </AlertDialogHeader>

          <AlertDialogBody className="pt-4">
            <ScrollView style={{ maxHeight: 500 }}>
              <VStack className="gap-10">
                {draft.map((d, index) => {
                  const pct = Math.round(d.weight * 100);
                  return (
                    <HStack className="items-center gap-4" key={d.label}>
                      <Text
                        className="text-sm text-typography-600 w-32 shrink-0"
                        numberOfLines={1}
                      >
                        {d.label}
                      </Text>
                      <Box className="flex-1">
                        <Slider
                          maxValue={100}
                          minValue={1}
                          onChange={(v) => handleSliderChange(index, v)}
                          size="sm"
                          value={pct}
                        >
                          <SliderTrack>
                            <SliderFilledTrack />
                          </SliderTrack>
                          <SliderThumb />
                        </Slider>
                      </Box>
                      <Text className="text-sm text-typography-600 w-10 shrink-0 text-right">
                        {i18n.numberToPercentage(pct, { precision: 0 })}
                      </Text>
                    </HStack>
                  );
                })}
              </VStack>
            </ScrollView>

            <HStack className="items-center gap-4 mt-10">
              <Text
                className="text-sm text-typography-600 w-32 shrink-0"
                numberOfLines={1}
              >
                {i18n.t("misc.otherValues")}
              </Text>
              <Box className="flex-1">
                <Slider
                  className="data-[disabled=true]:opacity-100"
                  isDisabled
                  maxValue={100}
                  minValue={0}
                  size="sm"
                  value={otherPct}
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb className="hidden" />
                </Slider>
              </Box>
              <Text className="text-sm text-typography-600 w-10 shrink-0 text-right">
                {i18n.numberToPercentage(otherPct, { precision: 0 })}
              </Text>
            </HStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <HStack className="gap-3 justify-end w-full">
              <Button onPress={handleClose} variant="outline">
                <ButtonText>{i18n.t("misc.cancel")}</ButtonText>
              </Button>
              <Button onPress={handleApply}>
                <ButtonText>{i18n.t("misc.apply")}</ButtonText>
              </Button>
            </HStack>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DemographicDistributionEditor;
